import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Edit2Icon,
  CopyIcon,
  TrashIcon,
  CalendarIcon,
  ClockIcon,
  MoreHorizontalIcon,
  CheckIcon,
  XIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CalendarDay, ScheduleType } from '@/types/schedule.types';

interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  selectedDays: CalendarDay[];
  onClose: () => void;
  onBulkEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  onSetScheduleType: (type: ScheduleType) => void;
}

const scheduleTypes: { value: ScheduleType; label: string; icon: React.ReactNode; color: string }[] = [
  {
    value: 'working',
    label: 'Рабочий день',
    icon: <ClockIcon className="h-4 w-4" />,
    color: 'text-green-600'
  },
  {
    value: 'night_shift',
    label: 'Ночная смена',
    icon: <ClockIcon className="h-4 w-4" />,
    color: 'text-indigo-600'
  },
  {
    value: 'day_off',
    label: 'Выходной',
    icon: <XIcon className="h-4 w-4" />,
    color: 'text-gray-600'
  },
  {
    value: 'holiday',
    label: 'Праздник',
    icon: <CalendarIcon className="h-4 w-4" />,
    color: 'text-red-600'
  },
  {
    value: 'reduced',
    label: 'Сокращённый',
    icon: <ClockIcon className="h-4 w-4" />,
    color: 'text-amber-600'
  }
];

export const ContextMenu: React.FC<ContextMenuProps> = ({
  isOpen,
  position,
  selectedDays,
  onClose,
  onBulkEdit,
  onCopy,
  onDelete,
  onSetScheduleType,
}) => {
  const [showScheduleTypes, setShowScheduleTypes] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const selectedCount = selectedDays.length;

  const menuItems = [
    {
      icon: <Edit2Icon className="h-4 w-4" />,
      label: `Редактировать (${selectedCount})`,
      onClick: onBulkEdit,
      disabled: selectedCount === 0
    },
    {
      icon: <CopyIcon className="h-4 w-4" />,
      label: 'Копировать настройки',
      onClick: onCopy,
      disabled: selectedCount !== 1
    },
    { type: 'separator' },
    {
      icon: <CalendarIcon className="h-4 w-4" />,
      label: 'Изменить тип дня',
      onClick: () => setShowScheduleTypes(!showScheduleTypes),
      hasSubmenu: true,
      disabled: selectedCount === 0
    },
    { type: 'separator' },
    {
      icon: <TrashIcon className="h-4 w-4" />,
      label: 'Сбросить к умолчанию',
      onClick: onDelete,
      disabled: selectedCount === 0,
      destructive: true
    }
  ];

  const adjustedPosition = {
    x: Math.min(position.x, window.innerWidth - 250),
    y: Math.min(position.y, window.innerHeight - 300)
  };

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-50 min-w-48 rounded-md border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      {menuItems.map((item, index) => {
        if (item.type === 'separator') {
          return <div key={index} className="mx-1 my-1 h-px bg-border" />;
        }

        return (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              if (!item.disabled && item.onClick) {
                item.onClick();
                if (!item.hasSubmenu) {
                  onClose();
                }
              }
            }}
            disabled={item.disabled}
            className={cn(
              'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:bg-accent focus:text-accent-foreground',
              item.disabled && 'pointer-events-none opacity-50',
              item.destructive && 'text-red-600 hover:bg-red-50 hover:text-red-700'
            )}
          >
            <span className="mr-2">{item.icon}</span>
            {item.label}
            {item.hasSubmenu && (
              <MoreHorizontalIcon className="ml-auto h-4 w-4" />
            )}
          </button>
        );
      })}

      {showScheduleTypes && (
        <div className="ml-4 mt-1 min-w-40 rounded-md border bg-popover p-1 shadow-lg">
          <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
            Тип рабочего дня
          </div>
          {scheduleTypes.map((type) => (
            <button
              key={type.value}
              onClick={(e) => {
                e.stopPropagation();
                onSetScheduleType(type.value);
                onClose();
              }}
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            >
              <span className={cn('mr-2', type.color)}>{type.icon}</span>
              {type.label}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};